import React from "react";
import { CHECK_TYPES, PASS_AT } from "@/shared/lib/evals-model";

import type { EvalCheckResult } from "@/shared/types";
// StatusPill/DeltaChip moved to shared/ for PublishGate; re-exported so the
// eval screens keep importing every atom from this one module.
export { StatusPill, DeltaChip } from "@/shared/components/evals-atoms";

/* Shared eval atoms. Scores are 0..1 everywhere, so one set of tones covers
   every check type and every screen — that uniformity is the entire payoff of
   forcing the range in the model. */

/* A pass is the expected case and gets quiet chrome. Only failure and partial
   credit spend color, so a wall of results reads as "these four are the
   problem" at a glance instead of as a christmas tree. */
/** How a score reads at a glance. `skip` is a null score — excluded from the
    average, so it gets no colour at all. */
export type ScoreTone = "pass" | "partial" | "fail" | "skip";

export function toneFor(score: number | null | undefined): ScoreTone {
  if (score == null) return "skip";
  if (score >= PASS_AT) return "pass";
  if (score > 0) return "partial";
  return "fail";
}

const CELL_TONES: Record<ScoreTone, string> = {
  pass: "bg-[var(--surface-2)] text-[var(--text-primary)]",
  partial: "bg-[var(--warn-bg)] text-[var(--warn-text)] ring-1 ring-[var(--warn-ring)]",
  fail: "bg-[var(--danger-bg)] text-[var(--danger)] ring-1 ring-[var(--danger-ring)]",
  skip: "text-[var(--text-quaternary)]",
};

/* Matrix cell — dense enough that 10 columns fit, legible enough to scan. */
export function ScorePill({ score, className = "" }: { score: number | null; className?: string }) {
  const tone = toneFor(score);
  return (
    <span
      className={`inline-flex items-center justify-center h-[21px] min-w-[38px] px-1.5 rounded-[7px] font-mono text-[11.5px] font-medium tabular-nums ${CELL_TONES[tone]} ${className}`}
      title={score == null ? "Skipped — excluded from the average" : `Score ${score.toFixed(2)}`}
    >
      {score == null ? "—" : score.toFixed(2)}
    </span>
  );
}

/* One check verdict with the quote that decided it. The evidence line is the
   whole reason anyone trusts the number — teams/contracts.js made the same call
   for handoff verification, and this renders the same shape. */
export function CheckRow({ result, className = "" }: { result: EvalCheckResult; className?: string }) {
  const tone = toneFor(result.score);
  return (
    <div className={`flex items-start gap-3 py-[9px] ${className}`}>
      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-1.5">
          <span className="text-[12.5px] font-medium text-[var(--text-primary)] truncate">
            {CHECK_TYPES[result.type]?.label || result.type}
          </span>
          <span className="font-mono text-[11px] text-[var(--text-quaternary)] truncate">{result.label}</span>
        </span>
        <span
          className={`block text-[11.5px] leading-[1.5] mt-[3px] ${
            tone === "fail" || tone === "partial" ? "text-[var(--text-secondary)]" : "text-[var(--text-quaternary)]"
          }`}
        >
          {result.evidence}
        </span>
      </span>
      <ScorePill score={result.score} className="mt-[1px]" />
    </div>
  );
}

/* Tag pill — cases carry tags instead of belonging to named datasets, so these
   are the only grouping affordance and they have to be clickable everywhere. */
export function TagChip({ tag, active = false, onClick }: {
  tag: string;
  active?: boolean;
  onClick?: () => void;
}) {
  const Node = onClick ? "button" : "span";
  return (
    <Node
      onClick={onClick}
      className={`shrink-0 h-[17px] px-[7px] rounded-full text-[10.5px] font-medium flex items-center ${onClick ? "press" : ""} ${
        active
          ? "bg-[var(--brand)] text-[var(--brand-foreground)]"
          : "bg-[var(--surface-2)] text-[var(--text-secondary)]"
      }`}
    >
      {tag}
    </Node>
  );
}
